import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/apiService";
import { formatDifficulty } from "../utils/formatters";
import AIDisclaimer from "../components/AIDisclaimer";
import "./css/EditQuestion.css";

function EditQuestion() {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [questionText, setQuestionText] = useState("");
  const [difficulty, setDifficulty] = useState("easy");
  const [bloomsTaxonomy, setBloomsTaxonomy] = useState("remember");
  const [options, setOptions] = useState([]);

  // Chatbot states
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [enhancedQuestion, setEnhancedQuestion] = useState(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  
  const chatMessagesEndRef = useRef(null);

  useEffect(() => {
    async function fetchQuestion() {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getQuestionById(questionId);
        setQuestion(data);
        setQuestionText(data.text || "");
        
        let difficultyValue = (data.difficulty || "easy").toLowerCase();
        if (difficultyValue === "hard") {
          difficultyValue = "difficult";
        }
        setDifficulty(difficultyValue);
        
        setBloomsTaxonomy(data.blooms_taxonomy || "remember");
        setOptions(data.options || []);
      } catch (err) {
        setError("Failed to load question details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchQuestion();
  }, [questionId]);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value
    };
    setOptions(updatedOptions);
  };

  const handleCorrectAnswerChange = (index) => {
    const updatedOptions = options.map((option, i) => ({
      ...option,
      is_correct: i === index
    }));
    setOptions(updatedOptions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        text: questionText,
        difficulty: difficulty,
        blooms_taxonomy: bloomsTaxonomy,
        options: options.map(opt => ({
          id: opt.id,
          text: opt.text,
          is_correct: opt.is_correct
        }))
      };

      await apiService.updateQuestion(questionId, payload);
      alert("Question updated successfully!");
      navigate(-1);
    } catch (err) {
      setError(err.message || "Failed to update question.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    const currentPrompt = chatInput;
    setChatInput("");
    setChatLoading(true);

    try {
      const response = await apiService.enhanceQuestion(questionId, currentPrompt);
      
      console.log('API Response:', response); // Debug log
      
      // Extract the question data from the response
      const enhancedData = response.question || response;
      
      // Store conversation ID if provided
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }
      
      setEnhancedQuestion(enhancedData);
      
      const aiMessage = {
        role: 'assistant',
        content: response.message || 'I have enhanced your question based on your request. Please review the changes below and decide whether to apply them or discard them.',
        enhancedData: enhancedData
      };
      
      setChatMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Enhancement error:', err); // Debug log
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${err.message || 'Failed to enhance question.'}`
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleApplyChanges = () => {
    console.log('Applying changes:', enhancedQuestion); // Debug log
    
    if (enhancedQuestion) {
      // Update the form fields with enhanced data
      if (enhancedQuestion.text) {
        setQuestionText(enhancedQuestion.text);
      }
      if (enhancedQuestion.difficulty) {
        // Normalize difficulty: convert 'hard' to 'difficult' to match dropdown values
        const normalizedDifficulty = enhancedQuestion.difficulty.toLowerCase() === 'hard' 
          ? 'difficult' 
          : enhancedQuestion.difficulty.toLowerCase();
        setDifficulty(normalizedDifficulty);
      }
      if (enhancedQuestion.blooms_taxonomy) {
        setBloomsTaxonomy(enhancedQuestion.blooms_taxonomy);
      }
      if (enhancedQuestion.options && Array.isArray(enhancedQuestion.options)) {
        // Map the options to ensure they have the correct structure
        const mappedOptions = enhancedQuestion.options.map((opt, idx) => ({
          id: options[idx]?.id || null, // Keep original ID if exists
          text: opt.text,
          is_correct: opt.is_correct
        }));
        setOptions(mappedOptions);
      }
      
      const confirmMessage = {
        role: 'system',
        content: '✅ Changes applied successfully! You can continue editing or save the question.'
      };
      setChatMessages(prev => [...prev, confirmMessage]);
      setEnhancedQuestion(null);
    }
  };

  const handleDiscardChanges = () => {
    const discardMessage = {
      role: 'system',
      content: '❌ Changes discarded. The question remains unchanged.'
    };
    setChatMessages(prev => [...prev, discardMessage]);
    setEnhancedQuestion(null);
  };

  const renderEnhancedPreview = (enhancedData) => {
    if (!enhancedData) return null;
    
    return (
      <div className="enhanced-preview">
        <h4>Enhanced Question Preview:</h4>
        <div className="preview-content">
          {enhancedData.text && (
            <p><strong>Question:</strong> {enhancedData.text}</p>
          )}
          {enhancedData.difficulty && (
            <p><strong>Difficulty:</strong> <span className="capitalize">{formatDifficulty(enhancedData.difficulty)}</span></p>
          )}
          {enhancedData.blooms_taxonomy && (
            <p><strong>Bloom's Taxonomy:</strong> <span className="capitalize">{enhancedData.blooms_taxonomy}</span></p>
          )}
          {enhancedData.outcomes && enhancedData.outcomes.length > 0 && (
            <p><strong>Learning Outcomes:</strong> {enhancedData.outcomes.map(o => o.short_name).join(', ')}</p>
          )}
          {enhancedData.options && enhancedData.options.length > 0 && (
            <div>
              <strong>Options:</strong>
              <ul>
                {enhancedData.options.map((opt, idx) => (
                  <li key={idx} style={{ color: opt.is_correct ? '#28a745' : 'inherit' }}>
                    {opt.text} {opt.is_correct && '✓'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="preview-actions">
          <button 
            type="button"
            className="btn btn-success" 
            onClick={handleApplyChanges}
          >
            ✓ Apply Changes
          </button>
          <button 
            type="button"
            className="btn btn-danger" 
            onClick={handleDiscardChanges}
          >
            ✗ Discard
          </button>
        </div>
      </div>
    );
  };

  if (loading) return <div>Loading question...</div>;
  if (error && !question) return <div>{error}</div>;
  if (!question) return <div>No question found.</div>;

  return (
    <div>
      <div className="course-detail-header">
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 style={{ marginTop: "1rem" }}>Edit Question</h1>
      </div>

      <div className="edit-question-layout">
        <div className="edit-question-container">
          {error && <div className="error-message">{error}</div>}
          
          {question.outcomes && question.outcomes.length > 0 && (
            <div className="question-outcomes-info">
              <strong>Learning Outcomes: </strong>
              {question.outcomes.map((outcome, idx) => (
                <span key={idx} className="badge badge-outcomes" style={{ marginLeft: '0.5rem', marginRight: '0.25rem' }}>
                  {outcome.short_name}
                </span>
              ))}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="edit-question-form">
            <div className="form-group">
              <label htmlFor="question-text">Question Text</label>
              <textarea
                id="question-text"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                className="form-textarea"
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="difficulty">Difficulty</label>
                <select
                  id="difficulty"
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="form-select"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="difficult">Hard</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="blooms-taxonomy">Bloom's Taxonomy</label>
                <select
                  id="blooms-taxonomy"
                  value={bloomsTaxonomy}
                  onChange={(e) => setBloomsTaxonomy(e.target.value)}
                  className="form-select"
                >
                  <option value="remember">Remember</option>
                  <option value="understand">Understand</option>
                  <option value="apply">Apply</option>
                  <option value="analyze">Analyze</option>
                  <option value="evaluate">Evaluate</option>
                  <option value="create">Create</option>
                </select>
              </div>
            </div>

            {question.type === 'multiple_choice' && options.length > 0 && (
              <div className="options-edit-section">
                <h3>Options</h3>
                {options.map((option, index) => (
                  <div key={option.id || index} className="option-edit-item">
                    <div className="option-edit-header">
                      <label>Option {String.fromCharCode(65 + index)}</label>
                      <label className="correct-checkbox">
                        <input
                          type="radio"
                          name="correct-answer"
                          checked={option.is_correct || false}
                          onChange={() => handleCorrectAnswerChange(index)}
                        />
                        <span>Correct Answer</span>
                      </label>
                    </div>
                    <input
                      type="text"
                      value={option.text || ""}
                      onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>

        {/* Chatbot Section */}
        <div className={`chatbot-container ${showChatbot ? 'expanded' : 'collapsed'}`}>
          <div className="chatbot-header" onClick={() => setShowChatbot(!showChatbot)}>
            <h3>🤖 AI Assistant</h3>
            <span className="chatbot-toggle">{showChatbot ? '−' : '+'}</span>
          </div>
          
          {showChatbot && (
            <div className="chatbot-content">
              <AIDisclaimer variant="compact" message="AI-generated suggestions may need verification." />
              <div className="chat-messages">
                {chatMessages.length === 0 && (
                  <div className="chat-welcome">
                    <p>👋 Hi! I can help you enhance this question.</p>
                    <p>Try asking me to:</p>
                    <ul>
                      <li>Make the question harder</li>
                      <li>Add more technical details</li>
                      <li>Simplify the language</li>
                      <li>Change the difficulty level</li>
                    </ul>
                  </div>
                )}
                
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`chat-message ${msg.role}`}>
                    <div className="message-content">
                      {msg.content}
                      {msg.enhancedData && renderEnhancedPreview(msg.enhancedData)}
                    </div>
                  </div>
                ))}
                
                {chatLoading && (
                  <div className="chat-message assistant">
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={chatMessagesEndRef} />
              </div>
              
              <form onSubmit={handleChatSubmit} className="chat-input-form">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask me to enhance the question..."
                  className="chat-input"
                  disabled={chatLoading}
                />
                <button 
                  type="submit" 
                  className="btn btn-primary chat-send-btn"
                  disabled={chatLoading || !chatInput.trim()}
                >
                  Send
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditQuestion;